import { createContext, useMemo } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormInput } from '@microservices/wiskey-react-components'
import { Box, Grid, Typography } from '@mui/material'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { EditConfigMenu } from '@components/ConfigEditMenu'
import { EditConfigScriptDialog } from '@components/EditConfigScriptDialog'

import { EditConfigProvider } from '@providers/EditConfigProvider'
import { GeneralButtons } from '@components/GeneralButtons'

import { usePrompt } from '@hooks'
import { ENTITY_CONFIG_TYPE, FLEXIBLE, GENERATOR_INPUT_TYPE } from '@constants'

import { useContextCreateOrEdit } from './hooks/useContextCreateOrEdit'
import { ContextTabs } from './components'
import { PageContextType } from './types'

export const PageContext = createContext<PageContextType>({} as PageContextType)

export const ContextCreateOrEdit = () => {
  const { t } = useTranslation()

  const {
    data: { methods, id, isCreate, isEdit, contextById, isLoadingContextById },
    state: { currentTab, isNewTitle, isLoading },
    handlers: { handleTabChange, handleCancel, handleSave },
  } = useContextCreateOrEdit()

  const {
    formState: { dirtyFields },
  } = methods

  const { ...dirtyFieldsCommon } = dirtyFields

  const dirty = useMemo(() => Object.values(dirtyFieldsCommon).some(Boolean), [dirtyFieldsCommon])

  const isLeaveEdit = dirty && !isLoading && !isCreate && !!contextById
  const isLeaveCreate = dirty && isCreate && !isEdit && !isLoading

  usePrompt({ when: isLeaveEdit || isLeaveCreate })

  const contextValue: PageContextType = {
    methods,
    id,
    isCreate,
  }

  const modelType = isCreate ? 'create' : 'edit'

  return (
    <PageContext.Provider value={contextValue}>
      <EditConfigProvider entityType={ENTITY_CONFIG_TYPE.CONTEXTS}>
        <PageContentLayout>
          <EditConfigScriptDialog />
          <Box sx={{ ...FLEXIBLE, overflow: 'auto', width: '100%' }}>
            <form>
              <FormProvider {...methods}>
                <Box p={1} pb={0}>
                  <Grid
                    container
                    alignItems={'center'}
                    justifyContent={'space-between'}
                    pb={2}
                    py={0.5}
                  >
                    <Typography variant='h5'>{t(`contexts.title.${modelType}`)}</Typography>
                    {id && <EditConfigMenu configId={id} />}
                  </Grid>
                  <FormInput
                    inputType={GENERATOR_INPUT_TYPE.INPUT}
                    label={t(`contexts.title.context`)}
                    name='title'
                    size='small'
                    sx={{ pt: 1 }}
                    rules={{
                      required: true,
                    }}
                  />
                </Box>
                <ContextTabs currentTab={currentTab} handleTabChange={handleTabChange} />
              </FormProvider>
            </form>
          </Box>
          <Grid container display='flex' flexDirection='row' justifyContent='flex-end' p='5px'>
            <GeneralButtons
              cancelText={t('contexts.cancelButton')}
              isLoading={isLoading || isLoadingContextById}
              saveDisabled={(!dirty && !isNewTitle) || isLoading || isLoadingContextById}
              saveText={t('contexts.saveButton')}
              onCancel={handleCancel}
              onSave={handleSave}
            />
          </Grid>
        </PageContentLayout>
      </EditConfigProvider>
    </PageContext.Provider>
  )
}
